// components/features/tournaments/components/EnhancedActionPanel.jsx
import React, { useState } from 'react';
import {
  CheckCircle,
  Edit3,
  AlertTriangle,
  Clock,
  Zap,
  FileText,
  Settings
} from '../../../common/Icons';
import { COMPARISON_TYPES, CONFLICT_SEVERITY } from '../../../../utils/comparisonEngine';
import { getActionButtonStyling } from '../../../../utils/conflictHighlighter';

const EnhancedActionPanel = ({ 
  analysis, 
  submission,
  onApprove, 
  onReject,
  onModify,
  isLoading = false,
  teamNames = {}
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [confirmationType, setConfirmationType] = useState('');

  const isPartial = analysis?.comparisonType === COMPARISON_TYPES.PARTIAL_SUBMISSION;
  const isConflicted = analysis?.comparisonType && analysis.severity !== CONFLICT_SEVERITY.NONE && !isPartial;
  const isPerfectMatch = analysis?.comparisonType === COMPARISON_TYPES.PERFECT_MATCH;
  const canAutoApprove = analysis?.autoApprovable || false;

  // Determine available actions based on analysis
  const getAvailableActions = () => {
    const actions = {
      canApprove: true,
      canReject: false, // Removed - doesn't make sense for tournament submissions
      canModify: true,
      approvalType: 'single', // 'single', 'conflict', 'auto'
      modifyOptions: []
    };

    if (isPerfectMatch || canAutoApprove) {
      actions.approvalType = 'auto';
      actions.modifyOptions = ['unified']; // Only "Modify Score" when submissions match
    } else if (isPartial) {
      actions.approvalType = 'single';
      actions.modifyOptions = ['unified']; // Single submission gets "Modify Score"
    } else if (isConflicted) {
      actions.approvalType = 'conflict';
      actions.modifyOptions = ['team1', 'team2']; // Only team-specific buttons when submissions differ
    } else {
      actions.modifyOptions = ['unified']; // Default to "Modify Score"
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  const handleApprove = async (submissionId = null) => {
    if (isConflicted && !selectedSubmission && !submissionId) {
      setShowConfirmation(true);
      setConfirmationType('select_required');
      return;
    }

    setShowConfirmation(true);
    setConfirmationType('approve');
  };

  const confirmApproval = async () => {
    try {
      await onApprove(submission.id, selectedSubmission);
      setShowConfirmation(false);
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error approving submission:', error);
    }
  };


  const handleModify = (mode) => {
    console.log('EnhancedActionPanel handleModify called with mode:', mode);
    console.log('Submission data:', submission);
    
    if (onModify) {
      let initialData;
      
      switch (mode) {
        case 'team1':
          initialData = submission.team1_submission_data;
          break;
        case 'team2':
          initialData = submission.team2_submission_data;
          break;
        case 'unified':
        default:
          // For unified mode, prefer the submission with more data, or use team1 as fallback
          const team1Data = submission.team1_submission_data;
          const team2Data = submission.team2_submission_data;
          
          if (team1Data && team2Data) {
            // If both exist, use the one with more player stats
            const team1PlayerCount = team1Data.playerStats?.length || team1Data.player_stats?.length || 0;
            const team2PlayerCount = team2Data.playerStats?.length || team2Data.player_stats?.length || 0;
            initialData = team1PlayerCount >= team2PlayerCount ? team1Data : team2Data;
          } else {
            initialData = team1Data || team2Data;
          }
          break;
      }
      
      console.log('Calling onModify with:', { submissionId: submission.id, mode, initialData });
      onModify(submission.id, mode, initialData);
    } else {
      console.error('onModify function not provided to EnhancedActionPanel');
    }
  };

  const ConfirmationModal = () => {
    if (!showConfirmation) return null;

    return (
      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
        <div className="bg-[#1e2129] border border-white/10 rounded-xl p-4 max-w-md w-full mx-4">
          {confirmationType === 'select_required' ? (
            <>
              <h3 className="text-sm font-bold text-white mb-2 flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                <span>Selection Required</span>
              </h3>
              <p className="text-sm text-gray-300 mb-2">
                The submissions conflict. Please select which submission to approve by clicking on it above, 
                then try approving again.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className={getActionButtonStyling('neutral', { variant: 'secondary' })}
                >
                  Got it
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-sm font-bold text-white mb-2 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Confirm Approval</span>
              </h3>
              <p className="text-sm text-gray-300 mb-2">
                {isPerfectMatch 
                  ? 'Both teams agree on all details. This will finalize the match result.'
                  : isPartial 
                    ? 'This will approve the single submission and finalize the match result.'
                    : selectedSubmission
                      ? `This will approve the selected submission and finalize the match result.`
                      : 'This will finalize the match result.'
                }
              </p>
              <div className="flex space-x-3 justify-end">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className={getActionButtonStyling('neutral', { variant: 'secondary' })}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApproval}
                  disabled={isLoading}
                  className={getActionButtonStyling('approve', { disabled: isLoading })}
                >
                  {isLoading ? 'Processing...' : 'Confirm Approval'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };


  const ConflictSelectionPanel = () => {
    if (!isConflicted || availableActions.approvalType !== 'conflict') return null;

    return (
      <div className="mb-2 p-3 bg-yellow-900/20 border border-yellow-500/40 rounded-lg">
        <h4 className="font-bold text-yellow-300 mb-2 flex items-center space-x-2">
          <AlertTriangle className="w-4 h-4" />
          <span>Conflict Resolution Required</span>
        </h4>
        <p className="text-yellow-300 text-xs mb-2">
          The teams submitted different results. Select which submission to approve:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {submission.team1_submission_data && (
            <button
              onClick={() => setSelectedSubmission(submission.team1_submission_id)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                selectedSubmission === submission.team1_submission_id
                  ? 'border-[#31C590]/40 bg-[#31C590]/10'
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-white">
                  {teamNames.team1 || 'Team 1'} Submission
                </span>
                {selectedSubmission === submission.team1_submission_id && (
                  <CheckCircle className="w-4 h-4 text-[#23F8B5]" />
                )}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Score: {submission.team1_submission_data.team1_score} - {submission.team1_submission_data.team2_score}
              </div>
            </button>
          )}

          {submission.team2_submission_data && (
            <button
              onClick={() => setSelectedSubmission(submission.team2_submission_id)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                selectedSubmission === submission.team2_submission_id
                  ? 'border-[#31C590]/40 bg-[#31C590]/10'
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-white">
                  {teamNames.team2 || 'Team 2'} Submission
                </span>
                {selectedSubmission === submission.team2_submission_id && (
                  <CheckCircle className="w-4 h-4 text-[#23F8B5]" />
                )}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Score: {submission.team2_submission_data.team1_score} - {submission.team2_submission_data.team2_score}
              </div>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative bg-[#191c23] border-t border-white/[0.06] p-4">

      {/* Conflict Selection Panel */}
      <ConflictSelectionPanel />

      {/* Smart Action Recommendations */}
      {analysis?.recommendations && analysis.recommendations.length > 0 && (
        <div className="mb-2 p-3 bg-[#22262f] border border-white/[0.07] rounded-lg">
          <h4 className="font-medium text-[#23F8B5] mb-2 flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>Recommended Actions:</span>
          </h4>
          <ul className="text-xs text-gray-300 space-y-1">
            {analysis.recommendations.slice(0, 2).map((recommendation, index) => (
              <li key={index} className="flex items-start space-x-2">
                <span className="text-[#23F8B5] mt-0.5">•</span>
                <span>{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 justify-end">
        
        {/* Modification Buttons */}
        {availableActions.canModify && (
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            {availableActions.modifyOptions.includes('team1') && (
              <button
                onClick={() => handleModify('team1')}
                disabled={isLoading}
                className={getActionButtonStyling('modify', { variant: 'secondary', disabled: isLoading })}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Modify {teamNames.team1 || 'Team 1'}
              </button>
            )}

            {availableActions.modifyOptions.includes('team2') && (
              <button
                onClick={() => handleModify('team2')}
                disabled={isLoading}
                className={getActionButtonStyling('modify', { variant: 'secondary', disabled: isLoading })}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Modify {teamNames.team2 || 'Team 2'}
              </button>
            )}

            {availableActions.modifyOptions.includes('unified') && (
              <button
                onClick={() => handleModify('unified')}
                disabled={isLoading}
                className={getActionButtonStyling('modify', { variant: 'secondary', disabled: isLoading })}
              >
                <Settings className="w-4 h-4 mr-2" />
                Modify Score
              </button>
            )}
          </div>
        )}

        {/* Primary Actions */}
        <div className="flex space-x-3">
          {/* Approve Button */}
          {availableActions.canApprove && (
            <button
              onClick={() => handleApprove()}
              disabled={isLoading || (isConflicted && !selectedSubmission)}
              className={getActionButtonStyling('approve', { 
                disabled: isLoading || (isConflicted && !selectedSubmission)
              })}
            >
              {canAutoApprove && <Zap className="w-4 h-4 mr-2" />}
              {!canAutoApprove && <CheckCircle className="w-4 h-4 mr-2" />}
              {isLoading 
                ? 'Processing...' 
                : canAutoApprove 
                  ? 'Auto-Approve'
                  : isPartial 
                    ? 'Approve Submission'
                    : isConflicted
                      ? 'Approve Selected'
                      : 'Approve'
              }
            </button>
          )}
        </div>
      </div>

      {/* Status Indicators */}
      <div className="mt-2 flex flex-wrap items-center justify-between text-xs text-gray-400">
        <div className="flex items-center space-x-4">
          {canAutoApprove && (
            <div className="flex items-center space-x-1">
              <Zap className="w-3 h-3 text-green-400" />
              <span>Auto-approvable</span>
            </div>
          )}

          {isPartial && (
            <div className="flex items-center space-x-1">
              <Clock className="w-3 h-3 text-[#23F8B5]" />
              <span>Partial submission</span>
            </div>
          )}

          {isConflicted && (
            <div className="flex items-center space-x-1">
              <AlertTriangle className="w-3 h-3 text-yellow-500" />
              <span>Requires selection</span>
            </div>
          )}
        </div>

        <div>
          {analysis?.severity && (
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              analysis.severity === CONFLICT_SEVERITY.NONE
                ? 'bg-green-900/30 text-green-300'
                : analysis.severity === CONFLICT_SEVERITY.LOW
                  ? 'bg-[#31C590]/10 text-[#23F8B5]'
                  : analysis.severity === CONFLICT_SEVERITY.MEDIUM
                    ? 'bg-yellow-900/30 text-yellow-300'
                    : 'bg-red-900/30 text-red-300'
            }`}>
              {analysis.severity.replace('_', ' ')} conflict
            </span>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmationModal />
    </div>
  );
};

export default EnhancedActionPanel;